// Phase 125-06 Task 1 — first-touch click-ID + UTM capture.
//
// Contract (125-CONTEXT.md §10 marketing attribution, §Pitfall 7 app-direct
// click-ID loss):
//   1. On app boot, read fbclid/gclid/ttclid + utm_* from
//      `window.location.search`.
//   2. Persist to localStorage under a single versioned key the FIRST time
//      we see a non-empty set. Subsequent boots NEVER overwrite — the
//      stored blob is the only source of truth for the person's first
//      touch from then on.
//   3. Register the stored click-IDs as PostHog super-properties on every
//      boot so every `capture()` from this session stamps the ad-platform
//      identifier. This handles the "marketing-site skipped, direct app
//      arrival with ?fbclid=..." case that would otherwise drop the
//      attribution chain.
//   4. `resolveFirstTouchSource()` returns the already-bucketed source for
//      the identify `$set_once.signup_source` write. Ladder:
//      fbclid → 'meta_ad' → gclid → 'google_ad' → ttclid → 'tiktok_ad' →
//      utm_source (verbatim) → document.referrer host → 'direct'.
//
// Failure modes are swallowed (JSON.parse / URL errors). PostHog analytics
// never blocks user-facing code — CLAUDE.md HARD RULE: observability
// fails-open.

import posthog from 'posthog-js';

const STORAGE_KEY = 'hookmyapp_first_touch_v1';

export interface FirstTouch {
  fbclid?: string;
  gclid?: string;
  ttclid?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  referrer_host?: string;
  /** Resolved first-touch bucket. Never null — falls back to 'direct'. */
  signup_source: string;
  /** ISO-8601. */
  captured_at: string;
}

function readFromUrl(): Partial<FirstTouch> {
  const params = new URLSearchParams(window.location.search);
  const out: Partial<FirstTouch> = {};
  const fbclid = params.get('fbclid');
  const gclid = params.get('gclid');
  const ttclid = params.get('ttclid');
  const utm_source = params.get('utm_source');
  const utm_medium = params.get('utm_medium');
  const utm_campaign = params.get('utm_campaign');
  if (fbclid) out.fbclid = fbclid;
  if (gclid) out.gclid = gclid;
  if (ttclid) out.ttclid = ttclid;
  if (utm_source) out.utm_source = utm_source;
  if (utm_medium) out.utm_medium = utm_medium;
  if (utm_campaign) out.utm_campaign = utm_campaign;
  return out;
}

function resolveReferrerHost(): string | undefined {
  if (!document.referrer) return undefined;
  try {
    return new URL(document.referrer).host;
  } catch {
    return undefined;
  }
}

function resolveSignupSource(touch: Partial<FirstTouch>): string {
  if (touch.fbclid) return 'meta_ad';
  if (touch.gclid) return 'google_ad';
  if (touch.ttclid) return 'tiktok_ad';
  if (touch.utm_source) return touch.utm_source;
  if (touch.referrer_host) return touch.referrer_host;
  return 'direct';
}

function registerClickIds(touch: FirstTouch): void {
  const props: Record<string, string> = {};
  if (touch.fbclid) props.fbclid = touch.fbclid;
  if (touch.gclid) props.gclid = touch.gclid;
  if (touch.ttclid) props.ttclid = touch.ttclid;
  if (touch.utm_source) props.utm_source = touch.utm_source;
  // posthog-js's register silently no-ops if called before init — safe.
  try {
    posthog.register(props);
  } catch {
    // fail-open
  }
}

/**
 * Read or initialize the first-touch blob and register its click-IDs as
 * PostHog super-properties. Call exactly once at app boot, AFTER
 * `posthog.init()` and AFTER the `register({ site, environment })` baseline
 * (so the site/env baseline wins on the rare collision).
 */
export function captureFirstTouch(): FirstTouch {
  try {
    const existing = localStorage.getItem(STORAGE_KEY);
    if (existing) {
      const parsed = JSON.parse(existing) as FirstTouch;
      // Re-register on every boot so in-memory super-properties stay
      // populated across page reloads.
      registerClickIds(parsed);
      return parsed;
    }
  } catch {
    // Fall through — treat malformed storage as "no first-touch yet".
  }

  const fromUrl = readFromUrl();
  const referrer_host = resolveReferrerHost();
  const touch: FirstTouch = {
    ...fromUrl,
    referrer_host,
    signup_source: resolveSignupSource({ ...fromUrl, referrer_host }),
    captured_at: new Date().toISOString(),
  };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(touch));
  } catch {
    // fail-open
  }
  registerClickIds(touch);
  return touch;
}

/**
 * Read the already-resolved signup_source from localStorage for use in the
 * identify `$set_once.signup_source` write. Safe to call before
 * `captureFirstTouch()` has run — returns 'direct' as the neutral default.
 */
export function resolveFirstTouchSource(): string {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return 'direct';
    const parsed = JSON.parse(raw) as FirstTouch;
    return parsed.signup_source ?? 'direct';
  } catch {
    return 'direct';
  }
}
